/* Phase 4 + S21 v3 · jp-product.js — PDP enhancements on top of Dawn machinery.
   Gallery arrows + stage tone (media alt #dark), qty stepper, size-guide
   dialog, sticky buy bar, share, ARIA tabs, cost-per-cut teaser (Intl),
   ATC live price, compact state mirror. Persona reactions (tuned note,
   rec chips, hints, pick badge) are pure CSS off body.persona-* — no JS.
   All binds idempotent (data flags) + re-run on shopify:section:load. */
(() => {
  if (window.__jpProductInit) return;
  window.__jpProductInit = true;

  const ready = (fn) =>
    document.readyState !== 'loading' ? fn() : document.addEventListener('DOMContentLoaded', fn);

  /* ---- money: format minor-unit cents via Intl (pattern: jp-cost-per-cut).
          Currency default digits respected; only force 3 when sub-minor-unit. ---- */
  const fmtMoney = (cents, currency) => {
    const lang = document.documentElement.lang || 'en';
    const value = cents / 100;
    const opts = { style: 'currency', currency };
    if (value > 0 && value < 0.01) { opts.minimumFractionDigits = 3; opts.maximumFractionDigits = 3; }
    try {
      return new Intl.NumberFormat(lang, opts).format(value);
    } catch { return value.toFixed(2) + ' ' + currency; }
  };

  /* ---- cost-per-cut teaser: cents ÷ (cuts × days × years).
          Server renders it [hidden]; reveal only once a real figure exists
          (Dawn 15 has no no-js class — this is the no-JS guard). ---- */
  const renderPercut = () => {
    document.querySelectorAll('[data-jp-percut]').forEach((el) => {
      const out = el.querySelector('[data-jp-percut-val]');
      if (!out) return;
      const cents = parseFloat(el.dataset.priceCents);
      const cuts = (parseFloat(el.dataset.cuts) || 8) * (parseFloat(el.dataset.days) || 312) * (parseFloat(el.dataset.years) || 10);
      if (!cents || !cuts || !el.dataset.currency) return;
      out.textContent = fmtMoney(cents / cuts, el.dataset.currency);
      el.hidden = false;
    });
  };

  /* ---- ATC label: Kit/Reserve swap + optional live price suffix ---- */
  const atcLabel = () => {
    const atcBtn = document.querySelector('[data-jp-atc]');
    if (!atcBtn) return;
    const span = atcBtn.querySelector('span');
    const stockBlock = document.querySelector('[id^="Inventory-"]');
    const priceNow = () => document.querySelector('[id^="price-"] .jp-pp__now');
    const withPrice = (label) => {
      if (!atcBtn.hasAttribute('data-jp-atc-price')) return label;
      const now = priceNow();
      return now ? label + ' — ' + now.textContent.trim() : label;
    };
    const current = () => {
      const pre = stockBlock && stockBlock.querySelector('.jp-pp__stock--pre');
      return withPrice(pre ? atcBtn.dataset.labelReserve : atcBtn.dataset.labelKit);
    };
    const sync = () => {
      /* Dawn's toggleSubmitButton writes window.variantStrings.addToCart on
         enable — keep that global equal to our full label so Dawn writes
         the brand text (incl. price) itself. */
      const label = current();
      if (window.variantStrings) window.variantStrings.addToCart = label;
      if (span && !atcBtn.hasAttribute('disabled')) span.textContent = label;
    };
    sync();
    if (stockBlock && !stockBlock.dataset.jpAtcBound) {
      stockBlock.dataset.jpAtcBound = '1';
      new MutationObserver(sync).observe(stockBlock, { childList: true, subtree: true });
    }
    return sync;
  };

  /* ---- compact state mirror in the rate row (Inventory- block is the
          swap target; the mirror echoes it after variant changes) ---- */
  const stateMirror = () => {
    const mirror = document.querySelector('[data-jp-state-mirror]');
    const stockBlock = document.querySelector('[id^="Inventory-"]');
    if (!mirror || !stockBlock) return;
    const sync = () => {
      const line = stockBlock.querySelector('.jp-pp__stock-line');
      if (!line) return;
      const key = line.classList.contains('jp-pp__stock--in') ? 'in'
        : line.classList.contains('jp-pp__stock--pre') ? 'pre' : 'out';
      mirror.className = 'jp-pp__state jp-pp__state--' + key;
      const t = mirror.querySelector('.t');
      if (t) t.textContent = line.textContent.replace(/\s+/g, ' ').trim();
    };
    if (!stockBlock.dataset.jpMirrorBound) {
      stockBlock.dataset.jpMirrorBound = '1';
      new MutationObserver(sync).observe(stockBlock, { childList: true, subtree: true });
    }
  };

  /* ---- C8 · dispatch date (a DATE, never a countdown) ----
     Compute the honest dispatch/arrival line from the store clock (cutoff + IANA tz) + the C1 transit window
     (single settings source, so it matches the shippingDetails schema). Server renders `dispatch_window` as
     the no-JS fallback. Left-handed selection overrides to the forged-to-order note (honest even if a lefty
     variant is mistakenly stock-tracked as available). Recomputes when Dawn re-renders the inventory block on
     variant change; the [data-jp-dispatch-done] flag makes the write idempotent so the observer can't loop. */
  const bizAdd = (date, n) => {
    let added = 0;
    while (added < n) {
      date.setUTCDate(date.getUTCDate() + 1);
      const wd = date.getUTCDay();
      if (wd !== 0 && wd !== 6) added += 1;
    }
    return date;
  };
  const applyDispatch = () => {
    const el = document.querySelector('[data-jp-dispatch]:not([data-jp-dispatch-done])');
    if (!el) return;
    el.setAttribute('data-jp-dispatch-done', '1');
    const out = el.querySelector('[data-jp-dispatch-txt]') || el;
    const d = el.dataset;
    const lang = document.documentElement.lang || 'en-GB';
    /* lefty = a stable data flag on the selected hand radio (multilingual-proof — never the display value) */
    const hand = document.querySelector('.jp-pp__handopt input[type="radio"]:checked');
    if (d.lefty && hand && hand.dataset.hand === 'left') { out.textContent = d.lefty; return; }
    try {
      const tz = d.tz || 'Europe/London';
      const tmin = parseInt(d.transitMin, 10) || 2;
      const tmax = parseInt(d.transitMax, 10) || 4;
      const parts = new Intl.DateTimeFormat('en-GB', { timeZone: tz, year: 'numeric', month: 'numeric', day: 'numeric', hour: 'numeric', minute: 'numeric', hour12: false }).formatToParts(new Date());
      const gp = (t) => parseInt((parts.find((p) => p.type === t) || {}).value, 10);
      let hh = gp('hour'); if (hh === 24) hh = 0;
      const mi = gp('minute');
      const dispatch = new Date(Date.UTC(gp('year'), gp('month') - 1, gp('day')));
      const wd = dispatch.getUTCDay();
      const isWeekday = wd !== 0 && wd !== 6;
      const cut = (d.cutoff || '14:00').split(':');
      const ch = parseInt(cut[0], 10);
      const cm = parseInt(cut[1], 10) || 0;
      /* the cutoff is the same-day-dispatch promise ("order by 2pm → ships today"); exclusive at the exact
         minute so 14:00 counts as after a 14:00 cutoff. Handling days are a SCHEMA concept (jp-schema-product
         handlingTime), deliberately NOT part of this on-page promise. */
      const beforeCutoff = hh < ch || (hh === ch && mi < cm);
      const today = isWeekday && beforeCutoff;
      if (!today) bizAdd(dispatch, 1); /* next business day */
      const arrMin = bizAdd(new Date(dispatch), tmin);
      const arrMax = bizAdd(new Date(dispatch), tmax);
      const wdName = new Intl.DateTimeFormat(lang, { timeZone: 'UTC', weekday: 'long' }).format(dispatch);
      const fmtD = (dt) => new Intl.DateTimeFormat(lang, { timeZone: 'UTC', day: 'numeric', month: 'short' }).format(dt);
      const dispatchText = today
        ? (d.tmplToday || 'Dispatches today').replace('[cutoff]', d.cutoff || '')
        : (d.tmplDay || 'Dispatches [day]').replace('[day]', wdName);
      const arrivesText = (d.tmplArrives || 'Arrives [start]–[end]').replace('[start]', fmtD(arrMin)).replace('[end]', fmtD(arrMax));
      out.textContent = '';
      const b = document.createElement('b');
      b.textContent = dispatchText;
      const sp = document.createElement('span');
      sp.className = 'jp-pp__dispatch-arr';
      sp.textContent = ' · ' + arrivesText;
      out.append(b, sp);
    } catch (e) { /* leave the server fallback in place */ }
  };
  const dispatchDate = () => {
    const stockBlock = document.querySelector('[id^="Inventory-"]');
    applyDispatch();
    if (stockBlock && !stockBlock.dataset.jpDispatchBound) {
      stockBlock.dataset.jpDispatchBound = '1';
      new MutationObserver(applyDispatch).observe(stockBlock, { childList: true, subtree: true });
    }
  };

  /* ---- A6 · student steer mini-card — 30-day dismiss + click/dismiss events ---- */
  const steerCard = () => {
    const card = document.querySelector('[data-jp-steer]');
    if (!card) return;
    const handle = card.dataset.handle || '';
    const key = 'jp_steer_dismissed_' + handle;
    const DAYS30 = 30 * 24 * 60 * 60 * 1000;
    /* re-hide runs UNCONDITIONALLY (before the bind guard) so a re-rendered card re-applies the dismiss state */
    try {
      const privacy = window.JapanScissors && window.JapanScissors.privacy;
      const ts = parseInt((privacy && privacy.getPreference(key)) || '0', 10);
      card.hidden = Boolean(ts && Date.now() - ts < DAYS30);
    } catch (e) { card.hidden = false; }
    if (card.dataset.jpSteerBound) return;
    card.dataset.jpSteerBound = '1';
    const link = card.querySelector('[data-jp-steer-link]');
    if (link) link.addEventListener('click', () => {
      try { document.dispatchEvent(new CustomEvent('jp:track', { detail: { event: 'jp_steer_click', handle: handle } })); } catch (e) { /* no-op */ }
    });
    const dismiss = card.querySelector('[data-jp-steer-dismiss]');
    if (dismiss) dismiss.addEventListener('click', () => {
      const privacy = window.JapanScissors && window.JapanScissors.privacy;
      if (privacy) privacy.setPreference(key, String(Date.now()));
      card.hidden = true;
      try { document.dispatchEvent(new CustomEvent('jp:track', { detail: { event: 'jp_steer_dismiss', handle: handle } })); } catch (e) { /* no-op */ }
    });
  };

  const init = () => {
    /* ---- gallery prev/next + stage tone ---- */
    document.querySelectorAll('media-gallery').forEach((gallery) => {
      /* CRIT-2: Dawn media-gallery.setActiveThumbnail() calls thumbnails.isSlideVisible()
         and .slider — slider-component members our plain-<div> thumbnail rail lacks, so
         Dawn throws on every thumb click / variant media change. Give the rail a no-op
         isSlideVisible() so Dawn sets aria-current then early-returns (the rail doesn't
         horizontally scroll). Idempotent; never edits the Dawn file. */
      const jpThumbs = gallery.querySelector('[id^="GalleryThumbnails"]');
      if (jpThumbs && typeof jpThumbs.isSlideVisible !== 'function') jpThumbs.isSlideVisible = () => true;
      const lis = () => [...gallery.querySelectorAll('.jp-pp__viewer li')];
      const stage = gallery.querySelector('[data-jp-stage]');
      const syncTone = () => {
        if (!stage) return;
        const active = lis().find((li) => li.classList.contains('is-active'));
        if (!active || !active.dataset.jpTone) return;
        const dark = active.dataset.jpTone === 'dark';
        if (stage.classList.contains('is-dark') === dark) return;
        if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
          stage.classList.toggle('is-dark', dark);
          return;
        }
        stage.classList.add('is-swapping');
        setTimeout(() => {
          stage.classList.toggle('is-dark', dark);
          stage.classList.remove('is-swapping');
        }, 160);
      };
      if (!gallery.dataset.jpGalBound) {
        gallery.dataset.jpGalBound = '1';
        const step = (dir) => {
          const items = lis();
          const current = Math.max(0, items.findIndex((li) => li.classList.contains('is-active')));
          const next = (current + dir + items.length) % items.length;
          gallery.setActiveMedia(items[next].dataset.mediaId, false);
        };
        gallery.querySelector('[data-jp-gallery-prev]')?.addEventListener('click', () => step(-1));
        gallery.querySelector('[data-jp-gallery-next]')?.addEventListener('click', () => step(1));

        const viewer = gallery.querySelector('.jp-pp__viewer');
        /* Dot pagination and swipe for multi-media products. Reuse the translated,
           media-specific thumbnail labels; keep the controls keyboard accessible. */
        const gItems = lis();
        let syncDots = () => {};
        if (stage && gItems.length > 1) {
          const dots = document.createElement('div');
          dots.className = 'jp-pp__dots';
          gItems.forEach((li) => {
            const b = document.createElement('button');
            b.type = 'button';
            b.className = 'jp-pp__gdot';
            const thumb = [...gallery.querySelectorAll('.jp-pp__thumbs li[data-target]')]
              .find(item => item.dataset.target === li.dataset.mediaId)?.querySelector('button');
            const label = thumb?.getAttribute('aria-label')?.trim();
            if (label) {
              b.setAttribute('aria-label', label);
            } else {
              // Preserve dot/media positions without exposing an unnamed control.
              b.disabled = true;
              b.tabIndex = -1;
              b.setAttribute('aria-hidden', 'true');
            }
            b.addEventListener('click', () => gallery.setActiveMedia(li.dataset.mediaId, false));
            dots.appendChild(b);
          });
          stage.appendChild(dots);
          syncDots = () => {
            /* re-query lis() (rather than the bind-time snapshot) so the active index stays correct even
               if the media set ever changes; the dot count is fixed but this is cheap + defensive */
            const cur = lis().findIndex((li) => li.classList.contains('is-active'));
            Array.prototype.forEach.call(dots.children, (d, i) => {
              d.classList.toggle('is-on', i === cur);
              d.setAttribute('aria-current', i === cur ? 'true' : 'false');
            });
          };
          syncDots();
          if (viewer && 'PointerEvent' in window) {
            let sx = 0, sy = 0, tracking = false;
            viewer.addEventListener('pointerdown', (e) => {
              if (e.pointerType === 'mouse' && e.button !== 0) return;
              sx = e.clientX; sy = e.clientY; tracking = true;
            });
            viewer.addEventListener('pointerup', (e) => {
              if (!tracking) return;
              tracking = false;
              const dx = e.clientX - sx, dy = e.clientY - sy;
              /* horizontal fling only — leaves taps (→ zoom) and vertical scrolls alone */
              if (Math.abs(dx) > 45 && Math.abs(dx) > Math.abs(dy) * 1.5) step(dx < 0 ? 1 : -1);
            });
            viewer.addEventListener('pointercancel', () => { tracking = false; });
          }
        }
        /* media-gallery toggles is-active on the lis (thumb clicks + variant media changes)
           — observe class flips (never patch Dawn), keep tone + dots in step */
        if (viewer) new MutationObserver(() => { syncTone(); syncDots(); }).observe(viewer, { attributes: true, attributeFilter: ['class'], subtree: true });
        syncTone();
      }
    });

    /* ---- qty stepper (document-level, bind once) ---- */
    /* reflect the lower bound on the minus button so at qty=min it reads as inert, not a silent no-op */
    const applyQtyRules = (inp) => {
      if (!inp) return;
      const cartQuantity = parseInt(inp.dataset.cartQuantity || '0', 10) || 0;
      const ruleMin = parseInt(inp.dataset.min || '1', 10) || 1;
      const ruleMax = inp.dataset.max == null ? null : parseInt(inp.dataset.max, 10);
      const step = parseInt(inp.step || '1', 10) || 1;
      const remainingMax = Number.isFinite(ruleMax) ? Math.max(0, ruleMax - cartQuantity) : null;
      let effectiveMin = ruleMin;
      if (cartQuantity >= ruleMin) effectiveMin = Math.min(effectiveMin, step);
      if (remainingMax !== null) effectiveMin = Math.min(effectiveMin, remainingMax);
      inp.min = effectiveMin;
      if (remainingMax !== null) inp.max = remainingMax;
      else inp.removeAttribute('max');
      inp.value = effectiveMin;
    };
    const syncQtyMin = (wrap) => {
      const inp = wrap?.querySelector('input');
      const minus = wrap?.querySelector('[data-jp-qty="-1"]');
      if (!inp || !minus) return;
      const value = parseInt(inp.value, 10) || parseInt(inp.min, 10) || 1;
      const min = parseInt(inp.min, 10) || 1;
      const max = inp.max === '' ? null : parseInt(inp.max, 10);
      const atMin = value <= min;
      minus.disabled = atMin;
      minus.setAttribute('aria-disabled', atMin ? 'true' : 'false');
      const plus = wrap?.querySelector('[data-jp-qty="1"]');
      const atMax = Number.isFinite(max) && value >= max;
      if (plus) {
        plus.disabled = atMax;
        plus.setAttribute('aria-disabled', atMax ? 'true' : 'false');
      }
    };
    document.querySelectorAll('.jp-pp__qty').forEach((wrap) => {
      const inp = wrap.querySelector('input');
      if (inp && !inp.dataset.jpRulesBound) {
        inp.dataset.jpRulesBound = '1';
        let queued = false;
        new MutationObserver(() => {
          if (queued) return;
          queued = true;
          queueMicrotask(() => {
            queued = false;
            applyQtyRules(inp);
            syncQtyMin(wrap);
          });
        }).observe(inp, { attributes: true, attributeFilter: ['data-cart-quantity', 'data-min', 'data-max', 'step'] });
      }
      applyQtyRules(inp);
      syncQtyMin(wrap);
    });
    if (!document.body.dataset.jpQtyBound) {
      document.body.dataset.jpQtyBound = '1';
      document.addEventListener('click', (e) => {
        const btn = e.target.closest('[data-jp-qty]');
        if (!btn) return;
        const wrap = btn.closest('.jp-pp__qty');
        const input = wrap?.querySelector('input');
        if (!input) return;
        const min = parseInt(input.min || '1', 10) || 1;
        const max = input.max === '' ? null : parseInt(input.max, 10);
        const step = parseInt(input.step || '1', 10) || 1;
        const direction = parseInt(btn.dataset.jpQty, 10) < 0 ? -1 : 1;
        let next = (parseInt(input.value, 10) || min) + direction * step;
        next = Math.max(min, next);
        if (Number.isFinite(max)) next = Math.min(max, next);
        input.value = next;
        input.dispatchEvent(new Event('change', { bubbles: true }));
        syncQtyMin(wrap);
      });
      document.addEventListener('change', (e) => {
        const inp = e.target.closest('.jp-pp__qty input');
        if (inp) {
          const min = parseInt(inp.min || '1', 10) || 1;
          const max = inp.max === '' ? null : parseInt(inp.max, 10);
          const step = parseInt(inp.step || '1', 10) || 1;
          let value = parseInt(inp.value, 10) || min;
          value = min + Math.max(0, Math.round((value - min) / step)) * step;
          if (Number.isFinite(max)) value = Math.min(max, value);
          inp.value = Math.max(min, value);
          syncQtyMin(inp.closest('.jp-pp__qty'));
        }
      });
    }

    /* size-guide JUMP link → smooth-scroll to the on-page #size-guide section (native anchor is the no-JS fallback) */
    document.querySelectorAll('[data-jp-guide-jump]').forEach((a) => {
      if (a.dataset.jpJumpBound) return;
      a.dataset.jpJumpBound = '1';
      a.addEventListener('click', (e) => {
        const t = document.getElementById('size-guide');
        if (!t) return;
        e.preventDefault();
        const reduce = window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
        t.scrollIntoView({ behavior: reduce ? 'auto' : 'smooth', block: 'start' });
        if (!t.hasAttribute('tabindex')) t.setAttribute('tabindex', '-1');
        t.focus({ preventScroll: true });
        /* S33 §2b · analytics — the jump link is the on-page size-guide's open path (same signal as the modal). Non-blocking. */
        try { document.dispatchEvent(new CustomEvent('jp:track', { detail: { event: 'jp_size_guide_opened' } })); } catch (err) { /* no-op */ }
      });
    });

    /* ---- size guide dialog ---- */
    const guide = document.querySelector('[data-jp-guide]');
    if (guide && !guide.dataset.jpBound) {
      guide.dataset.jpBound = '1';
      let lastFocus = null;
      /* isolate the background from AT swipe-nav while the guide is open (mirrors the zoom lightbox).
         The guide is NOT portaled to <body> — it lives inside <product-info> — so a flat "body > *"
         sweep would leave the whole PDP inside <product-info> interactive. Instead we walk from the
         guide up to <body>, inerting the SIBLINGS at every ancestor level (never an ancestor itself,
         which contains the guide). We record exactly which elements we set (skipping the scrim and any
         node already inert, e.g. by an open jp-layer or the zoom) and clear only those — so this never
         fights jp-layers' own inert bookkeeping and cleanly handles the nested-dialog case. */
      let guideInerted = [];
      const inertBg = (on) => {
        if (on) {
          guideInerted = [];
          let node = guide;
          while (node && node !== document.body && node.parentElement) {
            Array.prototype.forEach.call(node.parentElement.children, (sib) => {
              if (sib === node || sib.id === 'jp-scrim' || sib.inert) return;
              const tag = sib.tagName;
              if (tag === 'SCRIPT' || tag === 'STYLE' || tag === 'LINK' || tag === 'TEMPLATE') return;
              sib.inert = true;
              guideInerted.push(sib);
            });
            node = node.parentElement;
          }
        } else {
          guideInerted.forEach((el) => { el.inert = false; });
          guideInerted = [];
        }
      };
      let hideTimer = 0;
      const guideScrollKey = 'pdp-size-guide';
      const open = () => {
        lastFocus = document.activeElement;
        clearTimeout(hideTimer); // cancel a pending hide from a just-fired close() so guide.hidden and .is-open never disagree
        guide.hidden = false;
        requestAnimationFrame(() => guide.classList.add('is-open'));
        inertBg(true); // before focus moves into the dialog
        guide.querySelector('.jp-guide__close')?.focus();
        if (window.JapanScissors?.layers?.lockScroll) window.JapanScissors.layers.lockScroll(guideScrollKey);
        else document.body.classList.add('no-scroll');
        /* S33 §2b · analytics — size-guide modal opened (an engagement signal on the PDP). Non-blocking. */
        try { document.dispatchEvent(new CustomEvent('jp:track', { detail: { event: 'jp_size_guide_opened' } })); } catch (e) { /* no-op */ }
      };
      const close = () => {
        guide.classList.remove('is-open');
        if (window.JapanScissors?.layers?.unlockScroll) window.JapanScissors.layers.unlockScroll(guideScrollKey);
        else document.body.classList.remove('no-scroll');
        inertBg(false); // restore the background before focus returns to the opener
        clearTimeout(hideTimer);
        hideTimer = setTimeout(() => { guide.hidden = true; }, 300);
        lastFocus?.focus();
      };
      document.querySelectorAll('[data-jp-guide-open]').forEach((b) => b.addEventListener('click', open));
      guide.querySelectorAll('[data-jp-guide-close]').forEach((b) => b.addEventListener('click', close));
      document.addEventListener('keydown', (e) => {
        if (e.key === 'Escape' && !guide.hidden) close();
      });
      /* Tab focus trap — the dialog sets aria-modal="true"; keep focus inside it */
      guide.addEventListener('keydown', (e) => {
        if (e.key !== 'Tab' || guide.hidden) return;
        const f = [...guide.querySelectorAll('a[href], button:not([disabled]), input, select, textarea, [tabindex]:not([tabindex="-1"])')].filter((el) => el.offsetParent !== null);
        if (!f.length) return;
        const first = f[0], last = f[f.length - 1];
        if (e.shiftKey && document.activeElement === first) { e.preventDefault(); last.focus(); }
        else if (!e.shiftKey && document.activeElement === last) { e.preventDefault(); first.focus(); }
      });
      document.addEventListener('jp:layers:reset', () => {
        if (guide.hidden) return;
        clearTimeout(hideTimer);
        guide.classList.remove('is-open');
        guide.hidden = true;
        inertBg(false);
      });
    }

    /* ---- image zoom / lightbox (premium close-up for blade/finish detail) ---- */
    if (!document.body.dataset.jpZoomBound) {
      document.body.dataset.jpZoomBound = '1';
      let zoomReturn = null;
      const z = document.createElement('div');
      z.className = 'jp-zoom';
      z.setAttribute('role', 'dialog');
      z.setAttribute('aria-modal', 'true');
      z.hidden = true;
      z.innerHTML =
        '<div class="jp-zoom__scrim" data-jp-zoom-close></div>' +
        '<button type="button" class="jp-zoom__close" data-jp-zoom-close><svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path d="M6 6l12 12M18 6L6 18"></path></svg></button>' +
        '<img class="jp-zoom__img" alt="">';
      document.body.appendChild(z);
      // localized close label (en/fr/it) carried on the stage element, falls back to English
      const jpStageEl = document.querySelector('[data-jp-stage]');
      z.querySelector('.jp-zoom__close').setAttribute('aria-label', (jpStageEl && jpStageEl.dataset.closeLabel) || 'Close');
      // FR-JS gallery closeout: use the existing French locale's zoom-dialog wording.
      const zoomDialogLabel = /^fr(?:-|$)/i.test(document.documentElement.lang || '')
        ? 'Image produit en plein format'
        : ((jpStageEl && jpStageEl.dataset.zoomLabel) || 'Product image');
      z.setAttribute('aria-label', zoomDialogLabel);
      const zImg = z.querySelector('.jp-zoom__img');
      zImg.draggable = false;
      // touch-action inline (not only in the async-deferred stylesheet) so the browser never hijacks the first pinch/pan
      zImg.style.touchAction = 'none'; zImg.style.userSelect = 'none'; z.style.touchAction = 'none';

      /* ---- pinch-zoom + pan (S7): two-finger pinch, drag-pan when zoomed, dbl-tap/dbl-click/wheel
         to toggle. Tapping the image no longer dismisses — close is X · scrim · Esc only. ---- */
      const MAX_SCALE = 4, DBL_SCALE = 2.4;
      const reduceMo = window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
      let zs = 1, ztx = 0, zty = 0;   // live transform: scale + translate(px)
      let C0x = 0, C0y = 0;           // image centre in screen coords at identity (scaling keeps centre; translate shifts it)
      const applyT = (animate) => {
        zImg.style.transition = animate && !reduceMo ? 'transform .28s cubic-bezier(.32,.72,.25,1)' : 'none';
        zImg.style.transform = 'translate(' + ztx + 'px,' + zty + 'px) scale(' + zs + ')';
        zImg.style.cursor = zs > 1.02 ? 'grab' : 'zoom-in';
      };
      const resetT = () => { zs = 1; ztx = 0; zty = 0; zImg.style.transition = ''; zImg.style.transform = ''; zImg.style.cursor = ''; };
      const measureC0 = () => {
        const r = zImg.getBoundingClientRect();
        let tx = ztx, ty = zty;   // read the LIVE (possibly mid-animation) translate so C0 stays exact if a gesture interrupts an animated zoom
        const cs = getComputedStyle(zImg).transform;
        if (cs && cs !== 'none' && typeof DOMMatrixReadOnly === 'function') { const m = new DOMMatrixReadOnly(cs); tx = m.m41; ty = m.m42; }
        C0x = r.left + r.width / 2 - tx; C0y = r.top + r.height / 2 - ty;
      };
      const clampT = () => {
        let w = zImg.clientWidth, h = zImg.clientHeight;
        const nw = zImg.naturalWidth, nh = zImg.naturalHeight;   // discount object-fit:contain letterboxing
        if (nw && nh) { const bR = w / h, iR = nw / nh; if (iR > bR) h = w / iR; else w = h * iR; }
        const mx = Math.max(0, (w * zs - window.innerWidth) / 2 + 8);
        const my = Math.max(0, (h * zs - window.innerHeight) / 2 + 8);
        ztx = Math.min(mx, Math.max(-mx, ztx));
        zty = Math.min(my, Math.max(-my, zty));
      };
      const zoomToPoint = (fx, fy, target, animate) => {   // scale toward a screen focal point, keeping it fixed
        measureC0();
        const s1 = Math.min(MAX_SCALE, Math.max(1, target));
        ztx = fx - C0x - (s1 / zs) * (fx - C0x - ztx);
        zty = fy - C0y - (s1 / zs) * (fy - C0y - zty);
        zs = s1;
        if (zs <= 1.02) resetT(); else { clampT(); applyT(animate); }
      };

      const pts = new Map();   // active pointers: id -> {x,y}
      let pinch = null, panLast = null, moved = 0, tapT = 0, tapX = 0, tapY = 0;
      const clearGesture = () => { pts.clear(); pinch = null; panLast = null; moved = 0; tapT = 0; }; // wipe pointer/tap state between opens (called only on open/close, never mid-gesture)
      zImg.addEventListener('pointerdown', (e) => {
        if (z.hidden) return;
        zImg.setPointerCapture(e.pointerId);
        pts.set(e.pointerId, { x: e.clientX, y: e.clientY });
        moved = 0;
        if (pts.size === 2) {
          measureC0();
          const [a, b] = [...pts.values()];
          pinch = { d: Math.hypot(a.x - b.x, a.y - b.y) || 1, s0: zs, tx0: ztx, ty0: zty, fx: (a.x + b.x) / 2, fy: (a.y + b.y) / 2 };
          panLast = null;
        } else { panLast = { x: e.clientX, y: e.clientY }; }
      });
      zImg.addEventListener('pointermove', (e) => {
        const p = pts.get(e.pointerId);
        if (!p) return;
        moved += Math.hypot(e.clientX - p.x, e.clientY - p.y);
        p.x = e.clientX; p.y = e.clientY;
        if (pinch && pts.size >= 2) {
          const [a, b] = [...pts.values()];
          const d = Math.hypot(a.x - b.x, a.y - b.y) || 1;
          const fx = (a.x + b.x) / 2, fy = (a.y + b.y) / 2;
          const r = Math.min(MAX_SCALE, Math.max(1, pinch.s0 * d / pinch.d)) / pinch.s0;   // zoom ratio vs gesture start
          zs = pinch.s0 * r;
          ztx = fx - C0x - r * (pinch.fx - C0x - pinch.tx0);   // keep the pinch midpoint anchored (also pans with it)
          zty = fy - C0y - r * (pinch.fy - C0y - pinch.ty0);
          clampT(); applyT(false);
        } else if (panLast && zs > 1.02) {
          ztx += e.clientX - panLast.x; zty += e.clientY - panLast.y;
          panLast = { x: e.clientX, y: e.clientY };
          clampT(); applyT(false);
        }
      });
      const endPtr = (e) => {
        if (!pts.has(e.pointerId)) return;
        pts.delete(e.pointerId);
        if (pts.size < 2) pinch = null;
        if (pts.size === 1) { const [only] = [...pts.values()]; panLast = { x: only.x, y: only.y }; return; }
        if (pts.size > 0) return;
        panLast = null;
        if (zs <= 1.02) resetT(); else { clampT(); applyT(true); }
        if (moved < 8) {   // a genuine tap (not a drag/pinch): dbl-tap toggles zoom; single tap never dismisses
          const now = (window.performance && performance.now) ? performance.now() : 0;
          if (now && now - tapT < 300 && Math.abs(e.clientX - tapX) < 30 && Math.abs(e.clientY - tapY) < 30) {
            tapT = 0;
            if (zs > 1.02) resetT(); else zoomToPoint(e.clientX, e.clientY, DBL_SCALE, true);
          } else { tapT = now; tapX = e.clientX; tapY = e.clientY; }
        }
      };
      zImg.addEventListener('pointerup', endPtr);
      zImg.addEventListener('pointercancel', endPtr);
      z.addEventListener('wheel', (e) => {
        if (z.hidden) return;
        e.preventDefault();
        zoomToPoint(e.clientX, e.clientY, zs * (e.deltaY < 0 ? 1.15 : 1 / 1.15), false);
      }, { passive: false });
      const onResize = () => { if (z.hidden || zs <= 1.02) return; measureC0(); clampT(); applyT(false); }; // re-fit a zoomed lightbox after rotate/resize (bound only while open)
      let zoomInerted = [];
      const zoomScrollKey = 'pdp-zoom';
      const inertBg = (on) => { // isolate the fullscreen lightbox from AT swipe-nav (focus-trap covers keyboard); only touch elements we set, so it never fights jp-layers
        if (on) { zoomInerted = [...document.querySelectorAll('body > *')].filter((el) => el !== z && !el.inert); zoomInerted.forEach((el) => { el.inert = true; }); }
        else { zoomInerted.forEach((el) => { el.inert = false; }); zoomInerted = []; }
      };

      const closeZoom = () => {
        if (z.hidden) return;
        zImg.dataset.gen = String((+zImg.dataset.gen || 0) + 1); // invalidate any in-flight hi-res preloader for the closing image
        z.classList.remove('is-open');
        resetT();        // drop the pan/zoom transform so the CSS exit animation (current scale → .97) runs
        clearGesture();  // don't carry stale pointer/tap state into the next open
        window.removeEventListener('resize', onResize);
        inertBg(false); // restore the background before focus returns to the opener
        if (window.JapanScissors?.layers?.unlockScroll) window.JapanScissors.layers.unlockScroll(zoomScrollKey);
        else document.body.classList.remove('no-scroll');
        // Removing is-open already hides this dialog (display:none). Complete closure now;
        // a delayed timer could otherwise erase a newly reopened image.
        z.hidden = true;
        zImg.removeAttribute('src');
        if (zoomReturn && zoomReturn.isConnected) zoomReturn.focus({ preventScroll: true });
      };
      document.addEventListener('click', (e) => {
        const trigger = e.target.closest('[data-jp-stage] .jp-pp__viewer li[data-jp-image].is-active > img');
        if (trigger && z.hidden) {
          zoomReturn = trigger; // Restore the actual image opener for pointer and keyboard users.
          const low = trigger.currentSrc || trigger.src;
          const hi = trigger.closest('li')?.dataset.zoomSrc;
          zImg.src = low; // show the already-cached image instantly...
          if (hi && hi !== low) { // ...then upgrade to the hi-res 2048px master once it loads (no blank flash)
            const gen = (zImg.dataset.gen = String((+zImg.dataset.gen || 0) + 1));
            const pre = new Image();
            pre.onload = () => { if (!z.hidden && zImg.dataset.gen === gen) zImg.src = hi; };
            pre.src = hi;
          }
          zImg.alt = trigger.alt || '';
          resetT();        // start every open at fit (scale 1, centred) so the entrance animation is clean
          clearGesture();  // fresh pointer/tap state (defends against a pointer leaked by a prior lossy close)
          window.addEventListener('resize', onResize);
          z.hidden = false;
          // The stylesheet keeps .jp-zoom display:none until this class exists.
          // Make it focusable synchronously before isolating the background and moving focus.
          z.classList.add('is-open');
          if (window.JapanScissors?.layers?.lockScroll) window.JapanScissors.layers.lockScroll(zoomScrollKey);
          else document.body.classList.add('no-scroll');
          inertBg(true); // AT can't wander behind the fullscreen zoom
          z.querySelector('.jp-zoom__close').focus({ preventScroll: true });
          /* S33 §2b · analytics — gallery zoom lightbox opened (blade/finish detail engagement). Non-blocking. */
          try { document.dispatchEvent(new CustomEvent('jp:track', { detail: { event: 'jp_zoom_opened' } })); } catch (err) { /* no-op */ }
          return;
        }
        if (!z.hidden && e.target.closest('[data-jp-zoom-close]')) closeZoom(); // NB: image tap no longer closes — it zooms/pans
      });
      document.addEventListener('keydown', (e) => { if (e.key === 'Escape') closeZoom(); });
      z.addEventListener('keydown', (e) => {
        if (z.hidden || e.key !== 'Tab') return;
        /* The lightbox deliberately exposes one keyboard control (Close); keep
           Tab on it instead of allowing focus to fall into browser chrome. */
        e.preventDefault();
        z.querySelector('.jp-zoom__close')?.focus();
      });
      document.addEventListener('jp:layers:reset', () => {
        if (z.hidden) return;
        z.classList.remove('is-open');
        resetT();
        clearGesture();
        window.removeEventListener('resize', onResize);
        inertBg(false);
        z.hidden = true;
        zImg.removeAttribute('src');
      });
      /* keyboard/AT entry point: Enter/Space on the focused active viewer image opens the SAME zoom as a
         pointer click (synthetic .click() reuses the delegated open path; activeElement → the image, so
         focus returns to it on close). Pointer users keep the plain click. */
      document.addEventListener('keydown', (e) => {
        if (!z.hidden) return;
        if (e.key !== 'Enter' && e.key !== ' ' && e.key !== 'Spacebar') return;
        const im = e.target.closest && e.target.closest('[data-jp-stage] .jp-pp__viewer li[data-jp-image].is-active > img');
        if (im) { e.preventDefault(); im.click(); }
      });
    }
    /* Make viewer images focusable + labelled as zoom triggers (runs each init — the gallery re-renders on
       section reload). Inactive slides are display:none, so only the ACTIVE image is ever a tab stop.
       aria-label keeps the product alt AND states the zoom affordance; recomputed each pass = idempotent. */
    const jpZoomStage = document.querySelector('[data-jp-stage]');
    if (jpZoomStage) {
      const jpZoomLbl = jpZoomStage.dataset.zoomLabel || 'Zoom image';
      jpZoomStage.querySelectorAll('.jp-pp__viewer li[data-jp-image] > img').forEach((im) => {
        im.setAttribute('tabindex', '0');
        im.setAttribute('role', 'button');
        im.setAttribute('aria-label', im.alt ? jpZoomLbl + ': ' + im.alt : jpZoomLbl);
      });
    }

    /* ---- ATC label + state mirror + percut (independent of sticky) ---- */
    atcLabel();
    stateMirror();
    renderPercut();
    dispatchDate();
    steerCard();

    /* ---- sticky bar: echo the SELECTED variant (size · handedness) so a buyer deep in the
       specs never silently adds the default variant on a £300 tool (P2.2) ---- */
    const updateStickyVar = () => {
      const el = document.querySelector('[data-jp-sticky-var]');
      const vs = document.querySelector('variant-selects');
      if (!el || !vs) return;
      const vals = [];
      /* scope to OUR option fieldsets so a third-party widget's injected fieldset can't pollute the echo */
      vs.querySelectorAll('fieldset.jp-pp__opt').forEach((fs) => {
        const c = fs.querySelector('input:checked') || fs.querySelector('select');
        if (c && c.value) vals.push(c.value.trim());
      });
      el.textContent = vals.join(' · ');
    };
    updateStickyVar();

    /* ---- P3.x · size-safety + availability layer ----------------------------------------------
       Two calm, honest jobs off the SELECTED variant, no urgency/countdown/%:
         · size-safety: on a multi-size tool, block a sticky-ATC add until a size is explicitly picked
           (the sticky bar hides the chips) — scroll + focus the fieldset with a "Choose your size" nudge.
         · availability: recompute the availability line per selected variant, accent the ATC by state
           (in-stock vs forged-to-order via tokens), prompt "choose a size to see availability" when
           unselected, and gently note "other sizes are in stock" when the pick is forged-to-order.
       Reads state from the already-rendered per-size dots (.jp-pp__dot--in/pre) so there is one
       source of truth (the server-rendered chips) and no re-derivation. */
    let jpSizeSafety = null;
    const buyPanel = document.querySelector('[data-jp-buy-panel]');
    /* Dawn replaces the whole <variant-selects> subtree on every variant change (global.js viewTransition),
       so re-query the nudge marker + its fieldset LIVE rather than caching a node that goes detached. */
    const sizeNudge = () => document.querySelector('[data-jp-size-nudge]');
    const sizeFieldset = () => { const n = sizeNudge(); return n ? n.closest('fieldset') : null; };
    if (buyPanel && !buyPanel.dataset.jpAvailBound) {
      buyPanel.dataset.jpAvailBound = '1';
      const strChoose = buyPanel.dataset.jpChooseSize || 'Choose your size';
      const strChooseAvail = buyPanel.dataset.jpChooseSizeAvail || 'Choose a size to see availability';
      const strOthers = buyPanel.dataset.jpAvailOthers || 'Other sizes are in stock';
      const availPrompt = document.querySelector('[data-jp-avail-prompt]');
      const mainAtcBtn = () => document.querySelector('[data-jp-atc]');

      /* size radios (genuine size option only — the nudge marker sits in that fieldset) */
      const sizeRadios = () => { const fs = sizeFieldset(); return fs ? [...fs.querySelectorAll('input[type="radio"]')] : []; };
      const hasMultiSize = () => sizeRadios().length > 1;
      /* "touched" = the shopper has fired a change on a size radio at least once this page-view.
         A page can load with a size pre-checked (Dawn), but that is NOT an explicit human choice,
         so we track intent separately from :checked. */
      let sizeTouched = false;
      const checkedSize = () => sizeRadios().find((r) => r.checked) || null;
      /* stock state of a size radio, read from its rendered dot: 'in' | 'pre' | 'out' | null */
      const radioState = (r) => {
        if (!r) return null;
        const dot = r.parentElement && r.parentElement.querySelector('.jp-pp__dot');
        if (!dot) return null;
        if (dot.classList.contains('jp-pp__dot--in')) return 'in';
        if (dot.classList.contains('jp-pp__dot--pre')) return 'pre';
        if (dot.classList.contains('jp-pp__dot--out')) return 'out';
        return null;
      };
      const anyOtherInStock = (exclude) =>
        sizeRadios().some((r) => r !== exclude && radioState(r) === 'in');

      /* current selected-variant availability state, preferring the authoritative stock block */
      const selectedState = () => {
        const stockBlock = document.querySelector('[id^="Inventory-"]');
        const line = stockBlock && stockBlock.querySelector('.jp-pp__stock-line');
        if (line) {
          if (line.classList.contains('jp-pp__stock--in')) return 'in';
          if (line.classList.contains('jp-pp__stock--pre')) return 'pre';
          return 'out';
        }
        return radioState(checkedSize());
      };

      /* accent the ATC (main + sticky) by state — calm token tint only, never a hard alarm */
      const accentAtc = (state) => {
        [mainAtcBtn(), document.querySelector('[data-jp-sticky-atc]')].forEach((btn) => {
          if (!btn) return;
          btn.classList.toggle('jp-atc--instock', state === 'in');
          btn.classList.toggle('jp-atc--forged', state === 'pre');
        });
      };

      /* recompute the JS availability prompt line + ATC accent for the current selection */
      const refreshAvailability = () => {
        /* while we're still asking a multi-size shopper to choose, DON'T accent the ATC to the default
           variant's state — an accent there would contradict the "choose a size" prompt. Accent only
           once the choice is real (touched), or when there's no size decision to make. */
        const asking = hasMultiSize() && !sizeTouched;
        accentAtc(asking ? null : selectedState());
        if (!availPrompt) return;
        /* prompt is only meaningful for real multi-size products */
        if (!hasMultiSize()) { availPrompt.hidden = true; availPrompt.textContent = ''; availPrompt.className = 'jp-pp__avail-prompt'; return; }
        /* T4: ask only when no size is visibly selected. A pre-selected size already shows its own
           stock line, so asking again would contradict it. Size-safety (sticky nudge) is unchanged. */
        if (asking && !checkedSize()) {
          /* no explicit choice yet — invite one rather than implying the default variant's state */
          availPrompt.textContent = strChooseAvail;
          availPrompt.className = 'jp-pp__avail-prompt jp-pp__avail-prompt--ask';
          availPrompt.hidden = false;
          return;
        }
        /* a size is chosen: if it's forged-to-order but stock exists elsewhere, gently surface that */
        if (selectedState() === 'pre' && anyOtherInStock(checkedSize())) {
          availPrompt.textContent = strOthers;
          availPrompt.className = 'jp-pp__avail-prompt jp-pp__avail-prompt--others';
          availPrompt.hidden = false;
          return;
        }
        availPrompt.hidden = true;
        availPrompt.textContent = '';
        availPrompt.className = 'jp-pp__avail-prompt';
      };

      /* mark intent + clear any pending nudge on an explicit size interaction. Delegate on buyPanel
         (Dawn never replaces it) and listen for BOTH change AND click on a size radio — re-confirming the
         pre-checked default fires only 'click', not 'change', and must still count as a genuine choice. */
      const markSizeTouched = (e) => {
        const r = e.target.closest && e.target.closest('input[type="radio"]');
        if (!r || !sizeRadios().includes(r)) return;
        sizeTouched = true;
        const n = sizeNudge();
        if (n) { n.hidden = true; n.textContent = ''; }
        refreshAvailability();
      };
      buyPanel.addEventListener('change', markSizeTouched);
      buyPanel.addEventListener('click', markSizeTouched);

      jpSizeSafety = {
        /* true only when a genuine multi-size product has had no explicit size choice yet */
        needsChoice: () => hasMultiSize() && !sizeTouched,
        promptChoice: () => {
          const n = sizeNudge();
          if (n) { n.textContent = strChoose; n.hidden = false; }
          if (availPrompt) refreshAvailability();
          const fs = sizeFieldset();
          if (fs) {
            const reduce = window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
            fs.scrollIntoView({ behavior: reduce ? 'auto' : 'smooth', block: 'center' });
            /* focus the checked radio (or the first) so keyboard/AT lands on the choice; delayed a touch so
               the smooth-scroll isn't yanked back by the focus jump */
            const target = checkedSize() || sizeRadios()[0];
            if (target) window.setTimeout(() => { try { target.focus({ preventScroll: true }); } catch (e) { target.focus(); } }, reduce ? 0 : 260);
          }
        },
        refresh: refreshAvailability,
      };

      /* the Inventory- block is Dawn's authoritative swap target for the selected variant's stock —
         observe it so the ATC accent + prompt recompute the moment the real state lands (independent
         of the size-radio change event, which fires before Dawn's fetch resolves). Idempotent. */
      const invBlock = document.querySelector('[id^="Inventory-"]');
      if (invBlock && !invBlock.dataset.jpAvailBound) {
        invBlock.dataset.jpAvailBound = '1';
        new MutationObserver(refreshAvailability).observe(invBlock, { childList: true, subtree: true });
      }
      refreshAvailability();
    }

    /* ---- shared price-block observer: sticky price + percut + ATC price ---- */
    const priceBlock = document.querySelector('[id^="price-"]');
    if (priceBlock && !priceBlock.dataset.jpPriceBound) {
      priceBlock.dataset.jpPriceBound = '1';
      new MutationObserver(() => {
        renderPercut();
        updateStickyVar();
        const sync = atcLabel();
        if (sync) sync();
        const now = priceBlock.querySelector('.jp-pp__now');
        const stickyPrice = document.querySelector('[data-jp-sticky] .jp-sticky__t > span:not([data-jp-sticky-var])');
        if (now && stickyPrice) {
          const note = stickyPrice.querySelector('small');
          stickyPrice.textContent = now.textContent.trim() + (note ? ' ' : '');
          if (note) stickyPrice.appendChild(note);
        }
        /* keep the sticky ATC in step with the real button (label + sold-out/disabled) */
        const mainAtc = document.querySelector('[id^="ProductSubmitButton-"]');
        const stickyAtc = document.querySelector('[data-jp-sticky-atc]');
        if (mainAtc && stickyAtc) {
          stickyAtc.disabled = mainAtc.disabled;
          const ml = mainAtc.querySelector('span');
          if (ml) stickyAtc.textContent = ml.textContent.trim().split(' — ')[0]; // base verb only — the price already shows in .jp-sticky__t
        }
      }).observe(priceBlock, { childList: true, subtree: true });
    }

    /* ---- sticky buy bar ---- */
    const sticky = document.querySelector('[data-jp-sticky]');
    const panel = document.querySelector('[data-jp-buy-panel]');
    if (sticky && panel && !sticky.dataset.jpBound && 'IntersectionObserver' in window) {
      sticky.dataset.jpBound = '1';
      const footer = document.querySelector('.footer');
      let pastPanel = false;    // scrolled below the buy panel (bar wants to show)
      let footerInView = false; // the site footer has entered the viewport
      const applySticky = () => {
        // Show once the buy panel scrolls off the top, but RETRACT as the footer enters
        // view so the fixed bar never clips the footer's last rows.
        const show = pastPanel && !footerInView;
        sticky.classList.toggle('is-visible', show);
        sticky.setAttribute('aria-hidden', show ? 'false' : 'true');
      };
      new IntersectionObserver(
        ([entry]) => {
          pastPanel = !entry.isIntersecting && entry.boundingClientRect.top < 0;
          applySticky();
        },
        { threshold: 0 }
      ).observe(panel);
      if (footer) {
        new IntersectionObserver(
          ([entry]) => { footerInView = entry.isIntersecting; applySticky(); },
          { threshold: 0 }
        ).observe(footer);
      }
      sticky.querySelector('[data-jp-sticky-atc]')?.addEventListener('click', () => {
        /* P3.x · size-safety: on a multi-size tool the sticky bar hides the size chips, so if the
           shopper has NOT explicitly chosen a size, don't silently add the default variant — send them
           to the size fieldset with a calm "Choose your size" nudge instead. Chosen → submit as normal. */
        if (jpSizeSafety && jpSizeSafety.needsChoice()) { jpSizeSafety.promptChoice(); return; }
        document.querySelector('[id^="ProductSubmitButton-"]')?.click();
      });
    }

    /* ---- P2.1 · notify-me: AJAX-submit the back-in-stock contact form so the shopper stays on the
       unavailable variant + gets an inline success; degrades to a full-page post without JS ---- */
    if (!window.__jpNotifyBound) {
      window.__jpNotifyBound = true;
      document.addEventListener('submit', (e) => {
        const form = e.target.closest('[data-jp-notify] form');
        if (!form) return;
        if (!form.checkValidity()) return; // let native validation show; never AJAX (or disable the button on) an invalid form
        e.preventDefault();
        const box = form.closest('[data-jp-notify]');
        const ok = box && box.querySelector('[data-jp-notify-ok]');
        const err = box && box.querySelector('[data-jp-notify-err]');
        const btn = form.querySelector('button[type="submit"]');
        if (err) err.hidden = true;
        if (btn) btn.disabled = true;
        fetch(form.action, { method: 'POST', body: new FormData(form), headers: { Accept: 'text/html' } })
          .then((r) => { if (!r.ok) throw new Error('http'); return r.text(); })
          .then((responseText) => {
            const responseDoc = new DOMParser().parseFromString(responseText, 'text/html');
            const responseForm = responseDoc.querySelector('#JpNotify');
            const confirmed = responseForm?.querySelector('.jp-pp__notify-ok[role="status"]');
            if (!confirmed) throw new Error('unconfirmed');
            form.hidden = true;
            if (ok) ok.hidden = false;
            try { document.dispatchEvent(new CustomEvent('jp:track', { detail: { event: 'jp_notify_submit' } })); } catch (e) { /* no-op */ }
          })
          .catch(() => { if (btn) btn.disabled = false; if (err) err.hidden = false; });
      });
    }

    /* ---- share ---- */
    document.querySelectorAll('[data-jp-share]').forEach((btn) => {
      if (btn.dataset.jpBound) return;
      btn.dataset.jpBound = '1';
      btn.addEventListener('click', async () => {
        const url = btn.dataset.shareUrl || window.location.href;
        try {
          if (navigator.share) {
            await navigator.share({ title: document.title, url });
          } else {
            await navigator.clipboard.writeText(url);
            const original = btn.innerHTML;
            btn.textContent = btn.dataset.copiedText || 'Link copied';
            setTimeout(() => { btn.innerHTML = original; }, 1600);
          }
        } catch {}
      });
    });

    /* ---- product tabs (ARIA tabs w/ arrow keys + #jp-reviews deep link) ---- */
    document.querySelectorAll('[data-jp-ptabs]').forEach((root) => {
      if (root.dataset.jpBound) return;
      root.dataset.jpBound = '1';
      const tabs = [...root.querySelectorAll('[role="tab"]')];
      const panels = [...root.querySelectorAll('[role="tabpanel"]')];
      const reduce = window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
      const select = (tab, focus = true) => {
        tabs.forEach((t) => {
          const on = t === tab;
          t.setAttribute('aria-selected', on ? 'true' : 'false');
          t.tabIndex = on ? 0 : -1;
        });
        panels.forEach((p) => { p.hidden = p.id !== tab.getAttribute('aria-controls'); });
        if (focus) tab.focus();
      };
      tabs.forEach((tab, i) => {
        tab.addEventListener('click', () => select(tab, false));
        tab.addEventListener('keydown', (e) => {
          const dir = { ArrowRight: 1, ArrowLeft: -1, Home: 0, End: 0 }[e.key];
          if (dir === undefined) return;
          e.preventDefault();
          if (e.key === 'Home') return select(tabs[0]);
          if (e.key === 'End') return select(tabs[tabs.length - 1]);
          select(tabs[(i + dir + tabs.length) % tabs.length]);
        });
      });
      const reviewsTab = tabs.find((t) => t.dataset.jpTab === 'reviews');
      if (reviewsTab) {
        const openReviews = () => {
          select(reviewsTab, false);
          root.scrollIntoView({ behavior: reduce ? 'auto' : 'smooth', block: 'start' });
        };
        if (window.location.hash === '#jp-reviews' || window.location.hash === '#jp-reviews-anchor') openReviews();
        document.querySelectorAll('[data-jp-open-reviews]').forEach((a) => {
          if (a.dataset.jpBound) return;
          a.dataset.jpBound = '1';
          a.addEventListener('click', (e) => { e.preventDefault(); openReviews(); });
        });
      }

      /* generic deep-links: [data-jp-open-tab="<key>"] selects the matching tab (specs, overview…).
         Used by jp-spec-strip's "Full specs" link; only binds when that tab exists, else the href scroll wins. */
      document.querySelectorAll('[data-jp-open-tab]').forEach((a) => {
        if (a.dataset.jpBound) return;
        const key = a.getAttribute('data-jp-open-tab');
        let target = tabs.find((t) => t.dataset.jpTab === key);
        /* fallback: if the requested 'specs' tab is disabled, deep-link to the first flow tab ('b1'…),
           which holds the spec detail — so "Full specs" never dumps the shopper on Overview (PDP-03) */
        if (!target && key === 'specs') target = tabs.find((t) => /^b\d+$/.test(t.dataset.jpTab || ''));
        if (!target) return;
        a.dataset.jpBound = '1';
        a.addEventListener('click', (e) => { e.preventDefault(); select(target, false); root.scrollIntoView({ behavior: reduce ? 'auto' : 'smooth', block: 'start' }); });
      });
    });

    /* #jp-reviews target may be a standalone section (P4c.3) — smooth-scroll links */
    const reviewsSection = document.getElementById('jp-reviews');
    if (reviewsSection) {
      document.querySelectorAll('[data-jp-open-reviews]').forEach((a) => {
        if (a.dataset.jpBound) return;
        a.dataset.jpBound = '1';
        a.addEventListener('click', (e) => {
          e.preventDefault();
          reviewsSection.scrollIntoView({ behavior: (window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches) ? 'auto' : 'smooth', block: 'start' });
        });
      });
    }
  };

  ready(init);
  document.addEventListener('shopify:section:load', () => init());
  document.addEventListener('jp:privacy:change', steerCard);
})();
